"use client";
import React, { useRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { TextBlockContent } from "./Text";
import type { ButtonConfig, ThemeVariant } from "@/types";
import { Image, type ImageProps } from "@/components/ui/Image";
import { motion } from "motion/react";
import { useAntiParallelScroll } from "@/lib/hooks";
import { HeadingTag, HeadingStyle } from "../ui/Heading";

const galleryTeaserVariants = cva("container", {
  variants: {
    variant: {
      light: "",
      dark: "",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface GalleryTeaserProps {
  // Text block props
  topline?: string;
  headline?: string;
  text?: string;
  headingTag?: HeadingTag;
  headingStyle?: HeadingStyle;
  button?: ButtonConfig;

  variant?: ThemeVariant;
  images: ImageProps[];
  className?: string;
}

export const GalleryTeaser: React.FC<GalleryTeaserProps> = ({
  topline,
  headline,
  text,
  headingTag = "h2",
  headingStyle = "h2",
  button,
  variant = "light",
  images,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { y1, y2 } = useAntiParallelScroll({ offset: 90, containerRef });

  // Split cards into two columns
  const column1Images = images.filter((_, index) => index % 2 === 0);
  const column2Images = images.filter((_, index) => index % 2 === 1);

  return (
    <div
      ref={containerRef}
      className={galleryTeaserVariants({ variant, className })}
    >
      {/* Text Block - 1/3 width, sticky on desktop */}
      <div className="col-span-full lg:col-span-5 flex flex-col justify-center">
        <TextBlockContent
          topline={topline}
          headline={headline}
          text={text}
          headingTag={headingTag}
          headingStyle={headingStyle}
          button={button}
          variant={variant}
        />
      </div>

      {/* Image Cards */}
      <div className="col-span-full lg:col-start-7 lg:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-md overflow-hidden py-3xl">
        {/* First column */}
        <motion.div
          style={{ y: y1 }}
          className="flex flex-col justify-center gap-md pt-0 md:pt-64"
        >
          {column1Images.map((image, index) => (
            <Image
              key={`col1-${index}`}
              rounded="md"
              objectFit="cover"
              className={`aspect-3/4 ${index > 0 ? "hidden md:block" : ""}`}
              {...image}
            />
          ))}
        </motion.div>

        {/* Second column - hidden on mobile */}
        <motion.div
          style={{ y: y2 }}
          className="hidden md:flex flex-col justify-center gap-md pt-0"
        >
          {column2Images.map((image, index) => (
            <Image
              key={`col2-${index}`}
              rounded="md"
              objectFit="cover"
              className="aspect-3/4"
              {...image}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};
